import React, { useState } from 'react';
import { CheckCircle2, FolderInput, Loader2, Mail, ShieldAlert, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { ApercuGroupe } from './KlarysEnvoiGroupe';
import { ApercuDateEffet } from './KlarysDateEffet';
import { ApercuMembre, ApercuPieces, ApercuSimulation } from './KlarysCreation';
import { ApercuImport, ApercuRapprochement, ApercuRecreer, ApercuRecus, ApercuReleve } from './KlarysImport';

const STATUTS = {
  en_attente: ['En attente de votre confirmation', 'border-amber-300 bg-amber-50'],
  en_cours: ['Exécution…', 'border-sky-300 bg-sky-50'],
  executee: ['Exécutée', 'border-emerald-300 bg-emerald-50'],
  echec: ['Échec', 'border-rose-300 bg-rose-50'],
  partiel: ['Classé dans la GED — envoi incomplet', 'border-amber-300 bg-amber-50'],
  programmee: ['Programmé — voir l\'onglet Tâches', 'border-violet-300 bg-violet-50'],
  annulee: ['Annulée', 'border-slate-200 bg-slate-50'],
};

const ApercuTransmission = ({ c, id }) => (
  <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-700" data-testid={`klarys-transmission-${id}`}>
    <p className="flex items-center gap-1.5"><FolderInput className="h-3.5 w-3.5 text-[#0a3d62]" />
      GED : <b data-testid={`klarys-transmission-ged-${id}`}>{c.membre_nom}{c.numero_membre ? ` (${c.numero_membre})` : ''} / {c.sous_dossier}</b>
      <span className="text-slate-400">— copie, non visible dans l'espace membre</span></p>
    {c.envoyer && (
      <div className="border-t border-slate-100 pt-2">
        <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#0a3d62]" /> À : <b data-testid={`klarys-transmission-email-${id}`}>{c.email}</b>
          {!c.email_fiche && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-semibold text-amber-800" data-testid={`klarys-transmission-autre-adresse-${id}`}>autre adresse que la fiche</span>}</p>
        <p className="mt-1">Objet : <b data-testid={`klarys-transmission-objet-${id}`}>{c.objet}</b></p>
        <p className="mt-1.5 max-h-56 overflow-y-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-slate-800" data-testid={`klarys-transmission-message-${id}`}>{c.message}</p>
        <p className="mt-1 text-slate-500">Pièce jointe : {c.fichier_nom}</p>
      </div>
    )}
  </div>
);

export const KlarysActionCard = ({ action, onConfirm, onCancel, disabled }) => {
  const [details, setDetails] = useState(false);
  const imp = action.methode === 'IMPORT_MEMBRES';
  const rappro = action.methode === 'RAPPROCHEMENT';
  const releve = action.methode === 'RAPPROCHEMENT_RELEVE';
  const recus = action.methode === 'ENVOI_RECUS';
  const recreer = action.methode === 'RECREER_RECUS';
  const [exclus, setExclus] = useState(action.exclus || (imp || releve || recus ? action.corps?.exclus_defaut : null) || []);
  const [choixReleve, setChoixReleve] = useState(action.choix_releve || {});
  const [choix, setChoix] = useState(action.choix || 0);
  const [mode, setMode] = useState(action.mode_choisi || action.corps?.mode || 'virement');
  const groupe = action.methode === 'DOCUMENT_GROUPE';
  const aucun = (groupe && (action.corps?.destinataires || []).length <= exclus.length)
    || (imp && !(action.corps?.lignes || []).some((l) => !l.erreur && !exclus.includes(l.idx)))
    || (releve && !(action.corps?.lignes || []).some((l) => l.candidats?.length && !exclus.includes(l.idx)))
    || (recus && !(action.corps?.destinataires || []).some((d) => d.email_ok && !exclus.includes(d.cle)))
    || (recreer && !(action.corps?.lignes || []).some((l) => !exclus.includes(l.ident)));
  const corpsConfirm = groupe || imp || recus || recreer ? { exclus } : rappro ? { choix, mode } : releve ? { exclus, choix: choixReleve } : undefined;
  const dateEffet = action.methode === 'DATE_EFFET';
  const [pret, setPret] = useState(false);
  const [mail, setMail] = useState(false);
  const [label, cls] = STATUTS[action.statut] || STATUTS.en_attente;
  const attente = action.statut === 'en_attente';
  return (
    <div className={`ml-9 rounded-xl border-2 p-3 text-sm ${cls}`} data-testid={`klarys-action-${action.id}`}>
      <div className="flex items-center gap-2 font-semibold text-slate-900">
        <ShieldAlert className="h-4 w-4 text-amber-600" /> {attente ? 'Action à confirmer' : 'Action'}
        <span className="ml-auto text-[11px] font-medium text-slate-600" data-testid={`klarys-action-statut-${action.id}`}>
          {label}{action.resultat_statut ? ` (HTTP ${action.resultat_statut})` : ''}
        </span>
      </div>
      <p className="mt-1.5 text-slate-800" data-testid={`klarys-action-resume-${action.id}`}>{action.resume}</p>
      {action.methode === 'DOCUMENT' && action.corps && <ApercuTransmission c={action.corps} id={action.id} />}
      {dateEffet && action.corps && <ApercuDateEffet c={action.corps} id={action.id} modifiable={attente && !disabled} pret={pret} setPret={setPret} email={mail} setEmail={setMail} />}
      {imp && action.corps && <ApercuImport c={action.corps} id={action.id} exclus={exclus} setExclus={setExclus} modifiable={attente && !disabled} />}
      {rappro && action.corps && <ApercuRapprochement c={action.corps} id={action.id} choix={choix} setChoix={setChoix} mode={mode} setMode={setMode} modifiable={attente && !disabled} />}
      {recreer && action.corps && <ApercuRecreer c={action.corps} id={action.id} exclus={exclus} setExclus={setExclus} modifiable={attente && !disabled} />}
      {recus && action.corps && <ApercuRecus c={action.corps} id={action.id} exclus={exclus} setExclus={setExclus} modifiable={attente && !disabled} />}
      {releve && action.corps && <ApercuReleve c={action.corps} id={action.id} exclus={exclus} setExclus={setExclus} choix={choixReleve} setChoix={setChoixReleve} modifiable={attente && !disabled} />}
      {action.methode === 'CREER_MEMBRE' && action.corps && <ApercuMembre c={action.corps} id={action.id} />}
      {action.methode === 'CLASSER_PIECES' && action.corps && <ApercuPieces c={action.corps} id={action.id} />}
      {action.methode === 'SIMULATION_RCDS' && action.corps && <ApercuSimulation c={action.corps} id={action.id} />}
      {groupe && action.corps && <ApercuGroupe c={action.corps} id={action.id} exclus={exclus} setExclus={setExclus} modifiable={attente && !disabled} />}
      <button type="button" onClick={() => setDetails(!details)} className="mt-1 text-[11px] text-slate-500 underline" data-testid={`klarys-action-details-${action.id}`}>
        {details ? 'Masquer' : 'Voir'} le détail technique
      </button>
      {details && (
        <pre className="mt-1 max-h-40 overflow-auto rounded bg-white/70 p-2 text-[11px] text-slate-700">
          {`${action.methode} ${action.chemin}\n${JSON.stringify({ params: action.params, corps: action.corps }, null, 2)}`}
        </pre>
      )}
      {attente && (
        <div className="mt-2.5 flex gap-2">
          <Button size="sm" disabled={disabled || aucun || (dateEffet && !pret)} onClick={() => onConfirm(action, dateEffet ? { envoyer_email: mail } : corpsConfirm)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`klarys-confirm-${action.id}`}>
            {disabled ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} {dateEffet ? 'Valider et rééditer' : imp ? 'Créer les membres cochés' : rappro ? 'Enregistrer le paiement' : releve ? 'Enregistrer les virements cochés' : recus ? 'Envoyer reçu + attestation' : recreer ? 'Recréer le reçu' : action.methode === 'CREER_MEMBRE' ? 'Créer et inviter' : action.methode === 'CLASSER_PIECES' ? 'Téléverser dans la GED' : action.methode === 'SIMULATION_RCDS' ? (action.corps?.envoyer ? 'Générer et envoyer le devis' : 'Générer le devis') : 'Confirmer'}
          </Button>
          <Button size="sm" variant="outline" disabled={disabled} onClick={() => onCancel(action)} data-testid={`klarys-cancel-${action.id}`}>
            <XCircle className="h-4 w-4 mr-1" /> Annuler
          </Button>
        </div>
      )}
    </div>
  );
};
